/* GASSISA
=========================================================================================
* Descripción: Tabla independiente con filtro por columna y ordenamiento SIN paginación.
*              Muestra la lista completa de registros en un contenedor con scroll interno.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 30/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 08/10/2026 | LIC. José Luis Aragón C.                       | Cabecero primary.main (igual que
 *              |            |                                  | demás buscadores)
 * ---------------------------------------------------------------------------------------
 */

import TextField from "../ui/TextField";
import { Fragment, useEffect, useMemo, useState, forwardRef } from "react";

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper, Grid, Button } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";
import getObjectValue from "lodash.get";
import { isNullOrEmpty } from "../../settings/utils";

const SizeSoftXTableSinPaginado = forwardRef((props, ref) => {
  const {
    data = [],
    columns,
    disableStickyHeader,
    setRow,
    defaultRow,
    row,
    id,
    fontSize = "medium",
    rowSpacing = 2,
    EspaciadorColumnas = 11,
    onRowDoubleClick,
    maxHeight,
  } = props;

  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState(true);
  const [filters, setFilters] = useState({});
  const [dataView, setDataView] = useState(Array.isArray(data) ? data : []);

  const getMaxWidthForColumn = (column) => {
    const safeData = Array.isArray(dataView) ? dataView : [];
    const maxDataWidth = safeData.reduce((maxWidth, dataRow) => {
      const cellValue = getObjectValue(dataRow, column.field) || "";
      return Math.max(
        maxWidth,
        cellValue.toString().length * EspaciadorColumnas
      );
    }, 0);
    const contentWidth = maxDataWidth > 100 ? maxDataWidth : 100;
    return column.width ? Math.max(contentWidth, column.width) : contentWidth;
  };

  const minTableWidth = useMemo(
    () => columns.reduce((acc, col) => acc + (col.width || 150), 0),
    [columns]
  );

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const applyFilters = (list, filtersObj) => {
    const entries = Object.entries(filtersObj || {}).filter(
      ([_, v]) => !isNullOrEmpty(v)
    );
    if (!entries.length) return list;

    return list.filter((row) =>
      entries.every(([field, value]) => {
        const cell = normalize(getObjectValue(row, field));
        const needle = normalize(value);
        return cell.includes(needle);
      })
    );
  };

  const handleGetListOrden = (dataList, fieldFilter, orderField) => {
    const list = Array.isArray(dataList) ? [...dataList] : [];

    return list.sort((a, b) => {
      const valueA = getObjectValue(a, fieldFilter);
      const valueB = getObjectValue(b, fieldFilter);
      if (typeof valueA === "string" && typeof valueB === "string") {
        return orderField
          ? valueA.toLowerCase() < valueB.toLowerCase()
            ? -1
            : 1
          : valueA.toLowerCase() > valueB.toLowerCase()
          ? -1
          : 1;
      }
      return orderField
        ? valueA < valueB
          ? -1
          : 1
        : valueA > valueB
        ? -1
        : 1;
    });
  };

  useEffect(() => {
    const base = Array.isArray(data) ? data : [];

    const filtered = applyFilters(base, filters);

    const ordered = orderBy
      ? handleGetListOrden(filtered, orderBy, order)
      : filtered;

    setDataView(ordered);
    // eslint-disable-next-line
  }, [data, filters, orderBy, order]);

  const handleClickOrder = (field) => {
    const orderField = field === orderBy ? !order : true;
    setOrderBy(field);
    setOrder(orderField);
  };

  const handleClickRow = (valueRow) => () => {
    if (!defaultRow) {
      setRow(valueRow);
      return;
    }
    if (!row) {
      setRow(valueRow);
      return;
    }
    if (valueRow[id] === row[id]) {
      setRow(defaultRow);
      return;
    }
    setRow(valueRow);
  };

  useEffect(() => {
    if (!row) return;
    const exists = dataView.some((r) => r[id] === row[id]);
    if (!exists) setRow(defaultRow || null);
    // eslint-disable-next-line
  }, [dataView]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!dataView.length) return;
      const currentIndex = dataView.findIndex((r) => r[id] === row?.[id]);

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (currentIndex === -1 || currentIndex === dataView.length - 1) {
          setRow(dataView[0]);
          return;
        }
        setRow(dataView[currentIndex + 1]);
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (currentIndex === -1 || currentIndex === 0) {
          setRow(dataView[dataView.length - 1]);
          return;
        }
        setRow(dataView[currentIndex - 1]);
      }
    };

    if (ref?.current) {
      ref.current.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      if (ref?.current) {
        ref.current.removeEventListener("keydown", handleKeyDown);
      }
    };
    // eslint-disable-next-line
  }, [dataView, row, id, ref, setRow]);

  return (
    <Fragment>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid item xs={12}>
          <TableContainer
            ref={ref}
            component={Paper}
            tabIndex={0}
            sx={{ outline: "none", overflow: "auto", maxHeight }}
          >
            <Table stickyHeader={!disableStickyHeader} style={{ minWidth: minTableWidth }}>
              <TableHead>
                <TableRow>
                  {columns.map((column, i) => (
                    <TableCell
                      key={"f-" + i}
                      style={{
                        minWidth: getMaxWidthForColumn(column),
                        padding: "2px 8px",
                      }}
                      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                    >
                      {column.disableFilter ? null : (
                        <TextField autoComplete="off"
                          variant="standard"
                          value={filters[column.field] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFilters((prev) => ({
                              ...prev,
                              [column.field]: val,
                            }));
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Filtrar..."
                          fullWidth
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            fontSize: "12px",
                            "& .MuiInputBase-input": { color: "primary.contrastText" },
                            "& .MuiInputBase-input::placeholder": { color: "primary.contrastText", opacity: 0.7 },
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {columns.map((column, i) => (
                    <TableCell
                      key={i}
                      style={{
                        minWidth: getMaxWidthForColumn(column),
                        whiteSpace: "nowrap",
                        fontSize,
                        padding: "4px 8px",
                      }}
                      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                    >
                      {!column.hideSort && (
                        <Button
                          style={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            fontSize,
                          }}
                          variant="text"
                          color="inherit"
                          onClick={() => handleClickOrder(column.field)}
                          startIcon={
                            orderBy === column.field ? (
                              order ? (
                                <ArrowUpwardIcon />
                              ) : (
                                <ArrowDownwardIcon />
                              )
                            ) : (
                              <ArrowUpwardIcon />
                            )
                          }
                        >
                          <Typography
                            variant="button"
                            sx={{ fontWeight: "600", color: "primary.contrastText" }}
                            style={{
                              fontSize,
                              margin: "none",
                              padding: "none",
                            }}
                          >
                            {column.title}
                          </Typography>
                        </Button>
                      )}
                      {column.showRenderHeader && column.renderHeader()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataView.length > 0 ? (
                  dataView.map((dataRow, i) => (
                    <TableRow
                      key={i}
                      onClick={handleClickRow(dataRow)}
                      onDoubleClick={() => onRowDoubleClick?.(dataRow)}
                      style={{ height: 6 + rowSpacing, fontSize, cursor: onRowDoubleClick ? "pointer" : "default" }}
                      className={
                        !row
                          ? "no-row"
                          : dataRow[id] === row[id]
                          ? "row"
                          : "no-row"
                      }
                    >
                      {columns.map((column, j) => (
                        <TableCell
                          key={j}
                          align={column.align || "left"}
                          style={{
                            fontSize,
                            padding: "4px 8px",
                            position: "relative",
                          }}
                        >
                          {!column.render ? (
                            <Typography>{dataRow[column.field]}</Typography>
                          ) : (
                            column.render(dataRow)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography>
                        No se encontraron registros para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Fragment>
  );
});

SizeSoftXTableSinPaginado.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      title: PropTypes.string,
      render: PropTypes.func,
      hideSort: PropTypes.bool,
      disableFilter: PropTypes.bool,
      width: PropTypes.number,
      align: PropTypes.string,
      showRenderHeader: PropTypes.bool,
    })
  ),
  data: PropTypes.array,
  id: PropTypes.string.isRequired,
  fontSize: PropTypes.oneOfType([
    PropTypes.oneOf(["small", "medium", "large"]),
    PropTypes.string,
  ]),
  rowSpacing: PropTypes.number,
  onRowDoubleClick: PropTypes.func,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default SizeSoftXTableSinPaginado;